import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  MAX_WORK_ITEM_FILE_BYTES,
  MAX_WORK_ITEM_FILES,
} from './workItemFileLimits';
import { wrapPendingFile, revokePending } from './workItemFileItems';

/**
 * Shared multi-file drop + picker. Display names are editable; stored objects stay unique.
 */
const WorkItemFileDrop = ({ files, onChange, disabled, className = '' }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(
    () => () => {
      (files || []).forEach(revokePending);
    },
    // revoke only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const addFiles = useCallback(
    (incoming) => {
      const next = [...(files || [])];
      const notes = [];
      for (const file of incoming || []) {
        if (next.length >= MAX_WORK_ITEM_FILES) {
          notes.push('At most 10 files.');
          break;
        }
        if (!file || file.size < 1 || file.size > MAX_WORK_ITEM_FILE_BYTES) {
          notes.push(`${file?.name || 'File'} must be between 1 byte and 25 MB.`);
          continue;
        }
        next.push(wrapPendingFile(file));
      }
      setRejectNote(notes[0] || '');
      onChange(next);
    },
    [files, onChange],
  );

  const setDisplayName = (idx, displayName) => {
    onChange(
      (files || []).map((item, i) =>
        i === idx ? { ...item, displayName: String(displayName || '').slice(0, 200) } : item,
      ),
    );
  };

  const removeAt = (idx) => {
    const item = files[idx];
    revokePending(item);
    onChange(files.filter((_, i) => i !== idx));
  };

  return (
    <div className={className}>
      <div
        className={`rounded-md border border-dashed px-3 py-2 text-xs ${
          dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'
        } ${disabled ? 'opacity-50' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!disabled) addFiles(Array.from(e.dataTransfer.files || []));
        }}>
        <p className='text-muted-foreground'>
          Drop files here, or choose (up to 10, 25 MB each). Same names are kept as separate files —
          rename the label if you want.
        </p>
        {rejectNote ? <p className='mt-1 text-destructive'>{rejectNote}</p> : null}
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='mt-2'
          disabled={disabled}
          onClick={() => inputRef.current?.click()}>
          <Paperclip size={14} strokeWidth={1.5} />
          Choose files
        </Button>
        <input
          ref={inputRef}
          type='file'
          multiple
          className='hidden'
          disabled={disabled}
          onChange={(e) => {
            addFiles(Array.from(e.target.files || []));
            e.target.value = '';
          }}
        />
      </div>
      {files?.length ? (
        <ul className='mt-2 space-y-2'>
          {files.map((item, idx) => (
            <li key={item.id || `${item.displayName}-${idx}`} className='flex items-center gap-2 text-xs'>
              {item.previewUrl ? (
                <img
                  src={item.previewUrl}
                  alt=''
                  className='h-12 w-12 shrink-0 rounded border border-border object-cover'
                />
              ) : (
                <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded border border-border bg-muted text-[10px] text-muted-foreground'>
                  file
                </span>
              )}
              <div className='min-w-0 flex-1'>
                <Input
                  value={item.displayName || ''}
                  disabled={disabled}
                  maxLength={200}
                  className='h-8 text-xs'
                  onChange={(e) => setDisplayName(idx, e.target.value)}
                  aria-label='File display name'
                />
                <p className='mt-0.5 truncate text-muted-foreground'>
                  {item.originalName || item.file?.name} ({Math.ceil((item.size || item.file?.size || 0) / 1024)} KB)
                </p>
              </div>
              <Button
                type='button'
                size='icon'
                variant='ghost'
                disabled={disabled}
                onClick={() => removeAt(idx)}
                aria-label='Remove pending file'>
                <X size={14} strokeWidth={1.5} />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default WorkItemFileDrop;

export { MAX_WORK_ITEM_FILES, MAX_WORK_ITEM_FILE_BYTES } from './workItemFileLimits';
